import { CircularProgress, Box } from "@mui/material";
import Edit2LineIcon from "remixicon-react/Edit2LineIcon";
import { useState, useCallback, useRef } from "react";
import useAuth from "$hooks/useAuth";
import JTooltip from "$components/JTooltip/JTooltip";
import { Button } from "$components";
import useDeletableProduct from "$hooks/Catalogue/useDeletableProduct";
import { CANT_BE_EDITED_MSG } from "./constant";

export const ActionButtons = ({
  product,
  togglePopin,
  setSelectedProduct,
  referencingCategory,
  isLoadingRefCategory,
  company,
}: any) => {
  const { isAdmin } = useAuth();
  const [hoveredProduct, setHoveredProduct] = useState(undefined);
  const hoverTimeoutRef = useRef(null);
  const isHoveringRef = useRef(false);

  const { resetCache } = useDeletableProduct(hoveredProduct, company?._id);

  const isInReferencingCategory =
    !!referencingCategory?.id &&
    product.categories.some((cat) => cat?._id === referencingCategory?.id);

  const cantBeEditedMsg = !company?.isActive
    ? CANT_BE_EDITED_MSG.disabledCompany
    : isInReferencingCategory
    ? CANT_BE_EDITED_MSG.inReferencingCategory
    : !product?.isActive
    ? CANT_BE_EDITED_MSG.disabledProduct
    : null;

  const cantBeEdited = !company?.isActive || isInReferencingCategory;

  // Improved mouse handlers with debounce
  const handleMouseEnter = useCallback(() => {
    if (isHoveringRef.current) return;
    isHoveringRef.current = true;

    // Only set hoveredProduct if it's different than the current one
    if (hoveredProduct?._id !== product._id) {
      // Clear any existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }

      // Set a timeout to prevent rapid firing
      hoverTimeoutRef.current = setTimeout(() => {
        setHoveredProduct(product);
      }, 100);
    }
  }, [hoveredProduct, product]);

  const handleMouseLeave = useCallback(() => {
    isHoveringRef.current = false;

    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }

    // Set a timeout before clearing the hoveredProduct
    setTimeout(() => {
      // Only clear if we're still not hovering (prevents clearing during quick mouse movements)
      if (!isHoveringRef.current) {
        setHoveredProduct(null);
        // Reset the cache when mouse leaves so next hover will trigger API call
        resetCache();
      }
    }, 200);
  }, [resetCache]);

  // Optimized button click handler
  const handleEditClick = useCallback(
    (event) => {
      // Prevent focus issues
      event.currentTarget.blur();
      togglePopin();
      setSelectedProduct(product);
    },
    [product, setSelectedProduct, togglePopin]
  );

  return (
    <Box onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {isAdmin() && (
        <Box style={{ display: "flex", flexDirection: "row" }}>
          {isLoadingRefCategory ? (
            <CircularProgress />
          ) : (
            <JTooltip title={cantBeEditedMsg}>
              <Button
                color="secondary"
                onClick={handleEditClick}
                disabled={cantBeEdited}
                startIcon={<Edit2LineIcon />}
                style={{ width: 120 }}
              >
                Modifier
              </Button>
            </JTooltip>
          )}
        </Box>
      )}
    </Box>
  );
};
